import { useState, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import * as XLSX from 'xlsx';
import styles from './upload_modal.module.css';

const UploadModal = ({ isOpen, onClose, onUpload, isUploading = false }) => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  // Expected column headers
  const expectedHeaders = [
    'Account',
    'Item',
    'Type',
    'City',
    'Final length (km)',
    'Final length (meters)',
    'Unit Rate (meter)',
    'Total Cost without PST PKR',
    'Finalized Vendor',
    'PR',
    'PO',
    'PO Date',
    'Request Date',
  ];

  const validateFileFormat = async (selectedFile) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target.result;
          let workbook;
          let worksheet;

          // Check file extension
          const fileExtension = selectedFile.name
            .substring(selectedFile.name.lastIndexOf('.'))
            .toLowerCase();

          if (fileExtension === '.csv') {
            // Handle CSV file
            workbook = XLSX.read(data, { type: 'string', sheetRows: 1 });
          } else {
            // Handle Excel files
            workbook = XLSX.read(data, { type: 'array', sheetRows: 1 });
          }

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          worksheet = workbook.Sheets[firstSheetName];

          // Convert first row to JSON to get headers
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
          });

          if (jsonData.length === 0) {
            reject('File is empty');
            return;
          }

          // Get headers from first row
          const fileHeaders = jsonData[0].map((header) => String(header).trim());

          // Normalize headers for comparison (case-insensitive, trimmed)
          const normalizedExpected = expectedHeaders.map((h) => h.toLowerCase().trim());
          const normalizedFile = fileHeaders.map((h) => h.toLowerCase().trim());

          // Check if all expected headers are present
          // Note: Column order doesn't matter - we only check if all required columns exist
          const missingHeaders = [];
          normalizedExpected.forEach((expected) => {
            if (!normalizedFile.includes(expected)) {
              // Find the original header name (case-sensitive) for error message
              const originalIndex = normalizedExpected.indexOf(expected);
              missingHeaders.push(expectedHeaders[originalIndex]);
            }
          });

          if (missingHeaders.length > 0) {
            reject(`File format is incorrect. Missing columns: ${missingHeaders.join(', ')}`);
            return;
          }

          // All required headers are present (order doesn't matter)
          resolve(true);
        } catch (error) {
          reject('Error reading file. Please check the file format.');
        }
      };

      reader.onerror = () => {
        reject('Error reading file. Please try again.');
      };

      // Read file based on extension
      const fileExtension = selectedFile.name
        .substring(selectedFile.name.lastIndexOf('.'))
        .toLowerCase();

      if (fileExtension === '.csv') {
        reader.readAsText(selectedFile);
      } else {
        reader.readAsArrayBuffer(selectedFile);
      }
    });
  };

  const handleFileSelect = async (selectedFile) => {
    if (selectedFile) {
      // Check if file is Excel format
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = selectedFile.name
        .substring(selectedFile.name.lastIndexOf('.'))
        .toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setValidationError('Please upload a valid Excel file (.xlsx, .xls, or .csv)');
        setFile(null);
        return;
      }

      // Validate file format
      setValidationError(null);
      try {
        await validateFileFormat(selectedFile);
        setFile(selectedFile);
        setValidationError(null);
      } catch (error) {
        setValidationError(error);
        setFile(null);
      }
    }
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileSelect(selectedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!file) {
      setValidationError('Please select a file to upload');
      return;
    }

    if (onUpload) {
      await onUpload(file);
    }
  };

  const handleClose = () => {
    setFile(null);
    setIsDragging(false);
    setValidationError(null);
    onClose();
  };

  const handleDownloadTemplate = (e) => {
    e.stopPropagation();

    // Define the column headers matching the table
    const headers = [
      'Account',
      'Item',
      'Type',
      'City',
      'Final length (km)',
      'Final length (meters)',
      'Unit Rate (meter)',
      'Total Cost without PST PKR',
      'Finalized Vendor',
      'PR',
      'PO',
      'PO Date',
      'Request Date',
    ];

    // Create CSV content
    const csvContent = headers.join(',') + '\n';

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'Purchase_Order_Template.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles['modal-overlay']} onClick={handleClose}>
      <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles['modal-header']}>
          <h2 className={styles['modal-title']}>Upload</h2>
          <button className={styles['close-button']} onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles['modal-body']}>
          <div
            className={`${styles['upload-area']} ${isDragging ? styles['upload-area-dragging'] : ''} ${file ? styles['upload-area-has-file'] : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            <div className={styles['upload-icon-container']}>
              <AddIcon className={styles['upload-icon']} />
            </div>
            <p className={styles['upload-text']}>Click or drag to this area to upload</p>
            <p className={styles['download-text']}>
              Download format from{' '}
              <span className={styles['download-link']} onClick={handleDownloadTemplate}>
                here
              </span>
            </p>
          </div>
          {validationError && (
            <div className={styles['error-message']}>
              <p>{validationError}</p>
            </div>
          )}
          {file && (
            <div className={styles['file-display']}>
              <span className={styles['file-name']}>{file.name}</span>
              <button
                className={styles['remove-file-btn']}
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  setValidationError(null);
                }}
              >
                <CloseIcon />
              </button>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className={styles['modal-footer']}>
          <button
            className={styles['upload-btn']}
            onClick={handleUpload}
            disabled={isUploading || !file}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
